import DynamicImage from '@/components/commons/dynamic-image';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatDateUTC } from '@/lib/utils';
import { StoreCustomerOrderDetail } from '@/types/http';
import Link from 'next/link';

type PickupSectionProps = {
  dataOrder?: StoreCustomerOrderDetail;
};

export default function PickupSection({ dataOrder }: PickupSectionProps) {
  if (!dataOrder) return null;

  const {
    pickup_time,
    payment_method,
    location_name,
    location_address,
    token_pickup,
    token_pin,
    pinpoint,
  } = dataOrder;

  const parsedPinpoint: { lat: number; long: number } | null = (() => {
    try {
      return pinpoint ? JSON.parse(pinpoint) : null;
    } catch {
      return null;
    }
  })();

  const mapLink = parsedPinpoint
    ? `https://www.google.com/maps?q=${parsedPinpoint.lat},${parsedPinpoint.long}`
    : null;

  const hiddenStatuses = ['cancelled', 'cancel', 'done'];
  const shouldShowBarcode =
    token_pickup && !hiddenStatuses.includes(dataOrder.status.toLowerCase());

  return (
    <section className='mb-3 border-b border-gray-200 pb-3 xl:mb-6 xl:pb-6'>
      <h2 className='mb-4 text-lg font-bold xl:mb-6 xl:text-xl'>Pickup In Store</h2>

      <div className='mb-3 grid w-full gap-6 sm:grid-cols-2 xl:mb-6'>
        <InfoBlock title='Pickup Date' value={pickup_time ? formatDateUTC(pickup_time) : '-'} />
        <InfoBlock title='Payment Method' value={payment_method} capitalize />
      </div>

      <div className='grid w-full gap-6 sm:grid-cols-2'>
        <div className='flex flex-col text-sm xl:text-base'>
          <h3 className='font-bold'>Location</h3>
          <span>{location_name || '-'}</span>
          {mapLink ? (
            <Link href={mapLink} target='_blank' rel='noopener noreferrer' className='underline'>
              {location_address || '-'}
            </Link>
          ) : (
            <span>{location_address || '-'}</span>
          )}
        </div>

        {shouldShowBarcode && <BarcodePickup barcodeUrl={token_pickup} pin={token_pin} />}
      </div>
    </section>
  );
}

function InfoBlock({
  title,
  value,
  capitalize = false,
}: {
  title: string;
  value?: string;
  capitalize?: boolean;
}) {
  return (
    <div className={`flex flex-col text-sm xl:text-base ${capitalize ? 'capitalize' : ''}`}>
      <h3 className='font-bold'>{title}</h3>
      <span>{value || '-'}</span>
    </div>
  );
}

function BarcodePickup({ barcodeUrl, pin }: { barcodeUrl: string; pin?: string }) {
  return (
    <Collapsible className='mt-4 w-full'>
      <CollapsibleTrigger className='text-sm font-semibold underline'>
        Show Barcode Pickup
      </CollapsibleTrigger>
      <CollapsibleContent className='mt-2'>
        <div className='flex flex-col items-center md:items-start'>
          <div className='flex w-full justify-center p-4 sm:w-auto md:justify-start md:p-0'>
            <DynamicImage
              src={barcodeUrl}
              alt='Pickup Barcode'
              width={200}
              height={200}
              className='w-full rounded border border-gray-200 object-contain sm:h-[200px] sm:w-[200px] md:max-w-[200px]'
              sizes='100vw'
              unoptimized
            />
          </div>
          {pin && (
            <div className='mt-2 flex justify-center gap-2 text-lg font-bold tracking-widest sm:w-[200px] md:max-w-[200px] md:justify-center'>
              {pin.split('').map((char, idx) => (
                <span key={idx}>{char}</span>
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
